import React, { useEffect, useMemo, useState } from 'react';

import format from 'date-fns/format';
import setTime from 'date-fns/set';

import range from 'lodash/range';

import BasicDropdown from '../common/BasicDropdown/BasicDropdown';
import DayGridDropdown from './dropdowns/DayGridDropdown';
import IntervalTypeDropdown from './dropdowns/IntervalTypeDropdown';
import Label from '../common/Labels/Label';
import MonthGridDropdown from './dropdowns/MonthGridDropdown';
import TimeInput from '../common/TextInputs/TimeInput/TimeInput';
import WeekdayGridDropdown from './dropdowns/WeekdayGridDropdown';

import { INTERVAL_VALUES, generateCronStringFromInputs } from './utils/cron-utils';

import { MONTHS } from '../../constants/cron.constants';

const formatDateForTimeInput = date => format(date, 'HH:mm:ss');

const NumericDropdown = ({ values = [1], selectedValue = 1, onValueChanged = () => {} }) => {
  const dropdownVals = useMemo(() => {
    if (!values?.length) {
      return [{ id: 1, label: '1' }];
    }
    return values.map(val => ({
      id: val,
      label: `${val}`,
    }))
  }, [values]);
  
  return (
    <BasicDropdown 
      values={dropdownVals}
      selectedValue={selectedValue}
      onValueSelected={onValueChanged}
      hideLabelInSmallViewports={false}
      dropdownLabel=""
      buttonLabelSize="md"
    />
  );
}

const MinutesDropdown = ({ selectedValue = 1, onValueChanged = () => {} }) => {
  const MINUTES_VALUES = range(0, 60);
  return <NumericDropdown values={MINUTES_VALUES} selectedValue={selectedValue} onValueChanged={onValueChanged} />
}

export const CronGenerator = ({ onValueUpdate = () => {}, disabled = false }) => {
  const [selectedDayValues, setSelectedDayValues] = useState([1]);
  const [selectedDaysOfTheWeek, setSelectedDaysOfTheWeek] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [selectedMonthValues, setSelectedMonthValues] = useState(MONTHS);
  const [valuePerInterval, setValuePerInterval] = useState(1);
  const [selectedInterval, setSelectedInterval] = useState(INTERVAL_VALUES[0].id);

  const [fromTimeString, setFromTimeString] = useState(formatDateForTimeInput(setTime(new Date(), {
    hours: 8, minutes: 0, seconds: 0, milliseconds: 0,
  })));

  const [cronStr, setCronStr] = useState(() => generateCronStringFromInputs({
    valuePerInterval,
    intervalType: selectedInterval,
    fromTimeString,
    dayValues: selectedDayValues,
    weekdayValues: selectedDaysOfTheWeek,
    monthValues: selectedMonthValues,
  }));

  const showTimeInput = selectedInterval !== 'minutes' && selectedInterval !== 'hours';
  const showMonthlyControls = selectedInterval === 'months';
  const showWeeklyControls = selectedInterval === 'daysOfWeek';
  const showHourlyControls = selectedInterval === 'hours';
  const hideEveryStr = selectedInterval === 'minutes' || showHourlyControls;

  useEffect(() => {
    setCronStr(generateCronStringFromInputs({
      valuePerInterval,
      intervalType: selectedInterval,
      fromTimeString,
      dayValues: selectedDayValues,
      weekdayValues: selectedDaysOfTheWeek,
      monthValues: selectedMonthValues,
    }));
  }, [fromTimeString, selectedDayValues, selectedDaysOfTheWeek, selectedInterval, selectedMonthValues, valuePerInterval])

  useEffect(() => {
    if (onValueUpdate) {
      onValueUpdate(cronStr);
    }
  }, [cronStr, onValueUpdate])

  return (
    <div className="p-1 flex items-center justify-start">
      <IntervalTypeDropdown intervals={INTERVAL_VALUES} onValueUpdate={setSelectedInterval} disabled={disabled} />
      {
        !hideEveryStr && (
          <Label className="mr-1 ml-2" bold>
            {
              showWeeklyControls ? 'On' : 'Every'
            }
          </Label>
        )
      }
      {
        showHourlyControls && (
          <>
            <Label bold className="mx-2">on the</Label>
            <div className="w-20">
              <MinutesDropdown
                selectedValue={valuePerInterval}
                onValueChanged={setValuePerInterval}
              />
            </div>
            <Label bold className="mr-2">minute</Label>
          </>
        )
      }
      {
        showWeeklyControls && (
          <div className="mx-1">
            <WeekdayGridDropdown values={selectedDaysOfTheWeek} onValuesSelected={setSelectedDaysOfTheWeek} />
          </div>
        )
      }
      {
        showMonthlyControls && (
          <>
            <div className="mx-1">
              <MonthGridDropdown values={selectedMonthValues} onValuesSelected={setSelectedMonthValues} disabled={disabled} />
            </div>
            <Label className="mx-2" bold>on the</Label>
            <div className="mx-1">
              <DayGridDropdown values={selectedDayValues} onValuesSelected={setSelectedDayValues} disabled={disabled} />
            </div>
            <Label className="mx-1" bold>day,</Label>
          </>
        )
      }
      {
        showTimeInput && (
          <>
            <Label className="mr-2" bold>at</Label>
            <TimeInput
              className="w-32"
              step={60}
              value={fromTimeString}
              onChange={event => setFromTimeString(event.target.value)}
              disabled={disabled}
            />
          </>
        )
      }
    </div>
  )
}

export default CronGenerator;
